import React, { useState, useEffect } from "react"
import { Link, useParams, useHistory } from "react-router-dom"
import toast from "react-hot-toast"
import { Card, CardBody } from "reactstrap"

import "./style.scss"

import {
   GetOrderedTrailersDetails,
   UpdateOrderedTrailer,
   GetConfiguratorOrderPdf,
} from "api/api.service"
import { Button } from "components/Button"
import { Loader } from "components/Loader"
import { APP_ROUTES } from "helpers/routeHelpers"
import { AutoComplete } from "components/AutoComplete"
import ProductPricingDetails from "./ProductPricingDetails"
import ConfiguratorOrderStatus from "constants/ConfiguratorOrderStatus"
import { TrailerSize } from "constants"
import { formatNumber } from "utils/commonUtils"
import { calculatePackagePricing } from "utils/calculatePackagePricing"
import { downloadPdf } from "utils/downloadUtils"
import useConfiguratorCalculations from "hooks/useConfiguratorCalculations"
import autoCompleteStyles from "components/AutoComplete/autoCompleteStyles"

import mailIcon from "assets/images/mail.svg"
import phoneIcon from "assets/images/call.svg"
import locationIcon from "assets/images/location.svg"
import exportIcon from "assets/images/export.svg"

const statusOptions = Object.values(ConfiguratorOrderStatus).map(status => ({
   label: ConfiguratorOrderStatus.getDisplayTextKey(status),
   value: status,
}))

const ConfiguratorOrderDetails = () => {
   const { id } = useParams()
   const history = useHistory()

   const [orderData, setOrderData] = useState(null)
   const [isLoading, setIsLoading] = useState(true)
   const [currentStatus, setCurrentStatus] = useState(null)
   const [orderId, orderReferenceNo] = id.split("-")
   const [isDownloadPdfLoading, setIsDownloadPdfLoading] = useState(false)

   useEffect(() => {
      fetchOrderDetails()
   }, [orderId, orderReferenceNo])

   const fetchOrderDetails = async () => {
      try {
         setIsLoading(true)
         const response = await GetOrderedTrailersDetails(orderId, {
            referenceNo: orderReferenceNo,
         })
         setOrderData(response.data)
         setCurrentStatus({
            label: ConfiguratorOrderStatus.getDisplayTextKey(
               response.data.status
            ),
            value: response.data.status,
         })
      } catch (error) {
         console.error("Error fetching order details:", error)
         history.push(APP_ROUTES.TIMELINE_CONFIGURATOR_ORDERS)
      } finally {
         setIsLoading(false)
      }
   }

   const handleBack = () => {
      history.goBack()
   }

   const handleExportPdf = async () => {
      if (!orderData) {
         toast.error("No order data available for export")
         return
      }
      if (
         !orderData.note?.personalInfoForm?.address ||
         !orderData.note?.personalInfoForm?.email ||
         !orderData.note?.personalInfoForm?.firstName ||
         !orderData.note?.personalInfoForm?.lastName ||
         !orderData.note?.personalInfoForm?.phoneNumber
      ) {
         toast.error(
            "Customer information is incomplete. Please ensure all required fields are filled."
         )
         return
      }

      try {
         setIsDownloadPdfLoading(true)
         const calculations = useConfiguratorCalculations(orderData.note)

         const payload = {
            sendEmail: false,
            trailerId: orderData.configuratorTrailer?.id || 0,
            trailerImage:
               orderData.note?.trailerData?.mediaUrls?.[0] ||
               "default-image.jpg",
            trailerType:
               orderData.note?.trailerData?.trailerType?.trailerType ||
               "Standard",
            trailerModel:
               orderData.note?.trailerData?.model?.[0] || "Default Model",
            trailerSize:
               TrailerSize.getDisplayTextKey(
                  orderData.note?.trailerData?.configuratorTrailerSizeType?.[0]
               ) || "Standard",
            trailerPrice: formatNumber(calculations.trailerPrice),
            selectedCustomizations: Object.values(
               orderData.note.allSelectedCustomizePackages || {}
            ).map(item => ({
               name: item.name,
               price: formatNumber(Number(item.price)),
               variationsTotal: item.selectedVariations?.length
                  ? formatNumber(
                       item.selectedVariations.reduce(
                          (subSum, variation) =>
                             subSum + Number(variation.price),
                          Number(item.price)
                       )
                    )
                  : formatNumber(Number(item.price)),
               variations: item.selectedVariations?.length
                  ? item.selectedVariations.map(variationItem => ({
                       name: variationItem.name,
                       price: formatNumber(Number(variationItem.price)),
                    }))
                  : [],
            })),
            selectedCustomizationsTotalPrice: formatNumber(
               Number(calculations.totalCustomizePackagesPrice) +
                  Number(calculations.totalCustomizePackagesVariationPrice)
            ),
            selectedEquipments: Object.values(
               orderData.note.allSelectedEquipmentPackages || {}
            ).map(item => ({
               name: item.name,
               price: formatNumber(Number(item.price)),
               variationsTotal: item.selectedVariations?.length
                  ? formatNumber(
                       item.selectedVariations.reduce(
                          (subSum, variation) =>
                             subSum + Number(variation.price),
                          Number(item.price)
                       )
                    )
                  : formatNumber(Number(item.price)),
               variations: item.selectedVariations?.length
                  ? item.selectedVariations.map(variationItem => ({
                       name: variationItem.name,
                       price: formatNumber(Number(variationItem.price)),
                    }))
                  : [],
            })),
            selectedEquipmentsTotalPrice: formatNumber(
               Number(calculations.totalEquipmentPackagesPrice) +
                  Number(calculations.totalEquipmentPackagesVariationPrice)
            ),
            selectedPackages: Object.values(
               orderData.note.selectedPackages || {}
            ).map(item => {
               const isWholePackage =
                  item.selectedItems.length === Object.keys(item.items)?.length
               const { totalItemsPrice, mainPrice, discount } =
                  calculatePackagePricing({
                     price: item.price,
                     details: item.selectedItems,
                     hasWholePackage: isWholePackage,
                  })

               return {
                  allItemsSelectedOfPackage: isWholePackage,
                  name: item.packageName,
                  price: formatNumber(
                     (function () {
                        let itemsLength = Object.keys(item.items).length

                        if (item.selectedItems.length === itemsLength) {
                           return item.price || 0
                        } else if (item.selectedItems.length < itemsLength) {
                           return item.selectedItems.reduce((a, b) => {
                              return a + b?.itemPrice
                           }, 0)
                        }
                     })()
                  ),
                  packageItems: item.selectedItems.map(obj => ({
                     name: obj.name,
                     price: formatNumber(Number(obj.itemPrice)),
                  })),
                  packageSubtotal: formatNumber(totalItemsPrice),
                  discountAmount: discount,
               }
            }),
            selectedPackagesTotalPrice: formatNumber(
               Number(calculations.totalPackagePrice)
            ),
            totalCost: formatNumber(calculations.totalSum),
            customer: {
               address: orderData.note?.personalInfoForm?.address || "N/A",
               email: orderData.note?.personalInfoForm?.email || "N/A",
               firstName: orderData.note?.personalInfoForm?.firstName || "N/A",
               lastName: orderData.note?.personalInfoForm?.lastName || "N/A",
               phoneNumber:
                  orderData.note?.personalInfoForm?.phoneNumber || "N/A",
               note: orderData.note?.personalInfoForm?.note || "",
               zipCode: orderData.note?.personalInfoForm?.zip || "N/A",
               city: orderData.note?.personalInfoForm?.city || "N/A",
               state: orderData.note?.personalInfoForm?.state || "N/A",
            },
            selectedWarranty:
               orderData.note?.warrantyData &&
               orderData.note?.warrantyData.length > 0
                  ? orderData.note.warrantyData.map(warranty => ({
                       warrantyName: warranty?.configuratorWarranties?.name,
                       warrantyYear: Number(
                          warranty?.configuratorWarranties?.year
                       ),
                       warrantyPrice: formatNumber(
                          warranty?.configuratorWarranties?.price
                       ),
                       isDefault: warranty?.configuratorWarranties?.isDefault,
                    }))
                  : [],
            selectedWarrantyTotalPrice: formatNumber(
               calculations.warrantyPrice
            ),
            ...(!!orderData.note.downPaymentAmount && {
               downPaymentAmount: formatNumber(
                  orderData.note?.downPaymentAmount || 0
               ),
            }),
            ...(!!orderData.note.customerDownPaymentOffer && {
               customerDownPaymentOffer: formatNumber(
                  orderData.note?.customerDownPaymentOffer || 0
               ),
            }),
         }

         const response = await GetConfiguratorOrderPdf({ bodyData: payload })
         await downloadPdf(response)
         toast.success("PDF exported successfully!")
      } catch (error) {
         console.error("Error exporting PDF:", error)
         toast.error("Failed to export PDF. Please try again.")
      } finally {
         setIsDownloadPdfLoading(false)
      }
   }

   const handleStatusChange = async selectedOption => {
      if (!selectedOption || selectedOption.value === currentStatus?.value)
         return

      try {
         setCurrentStatus(selectedOption)
         await UpdateOrderedTrailer(orderId, { status: selectedOption.value })
         toast.success("Order status updated successfully!")
      } catch (error) {
         setCurrentStatus({
            label: ConfiguratorOrderStatus.getDisplayTextKey(orderData.status),
            value: orderData.status,
         })
         console.error("Error updating order status:", error)
      }
   }

   if (isLoading) {
      return (
         <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "400px" }}
         >
            <Loader />
         </div>
      )
   }

   if (!orderData) {
      return (
         <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "400px" }}
         >
            <div className="text-center">
               <h4>Order not found</h4>
               <Button title="Go Back" onClick={handleBack} />
            </div>
         </div>
      )
   }

   const { customer, configuratorTrailer } = orderData
   const trailerSize = TrailerSize.getDisplayTextKey(
      configuratorTrailer?.configuratorTrailerSizeType?.[0]
   )

   const trailerName =
      configuratorTrailer?.name ||
      `${trailerSize} ${
         configuratorTrailer?.configuratorTrailerType || ""
      }`.trim()

   return (
      <>
         <div className="configurator-order-details">
            <div className="order-header">
               <Link
                  to={APP_ROUTES.TIMELINE_CONFIGURATOR_ORDERS}
                  className="navigation-link"
               >
                  {"<"} Configurator Orders
               </Link>
               <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center gap-3">
                     <h2 className="order-title mb-0">{trailerName}</h2>
                  </div>
                  <div className="d-flex gap-3 align-items-center">
                     <div className="status-select-container">
                        <AutoComplete
                           value={currentStatus}
                           onChange={handleStatusChange}
                           isSearchable={false}
                           placeholder="Select Status"
                           customStyles={autoCompleteStyles.STYLE_SIX}
                           classNamePrefix="status-header-search-admin"
                           options={statusOptions}
                           className="min-200-w"
                           menuPortalTarget={document.body}
                        />
                     </div>
                     <Button
                        title={
                           <div className="d-flex align-items-center gap-2">
                              <img src={exportIcon} alt="export" />
                              Export Pdf
                           </div>
                        }
                        onClick={handleExportPdf}
                        className="export-button"
                        isLoading={isDownloadPdfLoading}
                        disabled={isDownloadPdfLoading}
                     />
                  </div>
               </div>
            </div>

            <div className="info-card">
               <Card className="customer-card">
                  <CardBody>
                     <h5 className="card-title">{`${customer?.firstName} ${customer?.lastName}`}</h5>
                     <div className="customer-info">
                        <div className="d-flex gap-4">
                           <div>
                              <img src={mailIcon} alt="mail" />
                              <p className="info-text">{customer?.email}</p>
                           </div>
                           <div>
                              <img src={phoneIcon} alt="phone" />
                              <p className="info-text">{customer?.phoneNo}</p>
                           </div>
                        </div>
                        <div>
                           <img src={locationIcon} alt="location" />
                           <p>{customer?.address}</p>
                        </div>
                     </div>
                  </CardBody>
               </Card>
            </div>

            <div className="summary-card">
               <Card>
                  <CardBody>
                     <h5 className="card-title">Order Summary</h5>
                     <ProductPricingDetails
                        data={orderData.note}
                        customerDownPaymentOffer={
                           orderData.note.customerDownPaymentOffer
                        }
                        downPaymentAmount={orderData.note.downPaymentAmount}
                     />
                  </CardBody>
               </Card>
            </div>
         </div>
      </>
   )
}

export default ConfiguratorOrderDetails
